import { createStructuredSelector } from 'reselect';
import { isTopicFeed } from '@utils/feedTypeHelper';
import { emptyPaginatedObject, emptyObject } from '@constants/empty';
import {
  getTopologySlug,
  getIsNoTestGoal,
  getTopologyCoursesCount,
  getTopologyName,
  getTopologyUpcomingCoursesCountThisMonth,
  getTopologyParentName
} from './topologies';
import { emptyExtraBlockInfoObject, getFeedRow } from './feedrows';

const getUid = (_, uid) => uid;

const topologyUidAndName = {
  uid: getUid,
  name: getTopologyName
};

const topologyBasicInfo = {
  ...topologyUidAndName,
  isNoTestGoal: getIsNoTestGoal,
  slug: getTopologySlug
};

/**
 * @param {Object} state - Entire store object
 * @param {String} topologyUID - uid of the required topology
 * @returns {Object} {uid, name, coursesCount, upcomingCoursesThisMonth, isNoTestGoal, slug}
 */
export const getTopologyInfo = createStructuredSelector({
  ...topologyBasicInfo,
  coursesCount: getTopologyCoursesCount,
  upcomingCoursesThisMonth: getTopologyUpcomingCoursesCountThisMonth
});

export const getTopologyFeed = (state, topologyUID) =>
  state.paginator.topologyFeed[topologyUID];

const getTopologyFeedOrEmptyObject = (state, topologyUID) =>
  getTopologyFeed(state, topologyUID) || emptyPaginatedObject;

export const getIsTopologyFeedLoaded = (state, topologyUID) =>
  getTopologyFeedOrEmptyObject(state, topologyUID).loaded;

export const getTopologyFeedResults = (state, topologyUID) =>
  getTopologyFeedOrEmptyObject(state, topologyUID).results;

export const getTopicFeedRowData = (state, topologyUID) => {
  const topicFeedRow = getTopologyFeedResults(state, topologyUID)
    .map((item) => getFeedRow(state, item.feedrow))
    .find(
      (feedRow) =>
        isTopicFeed(
          (feedRow.extraBlockinfo || emptyExtraBlockInfoObject).entity_type
        ) && feedRow.type !== 'topic_group_non_feed'
    );
  if (topicFeedRow) return topicFeedRow.data;
  return [];
};

// #region courses-page-selectors
export const getGoalInfoForCoursesPage =
  createStructuredSelector(topologyBasicInfo);

const getParentUidAndName = createStructuredSelector({
  uid: getUid,
  name: getTopologyParentName
});

export const getTopicInfoForCoursesPage = createStructuredSelector({
  ...topologyUidAndName,
  parent: getParentUidAndName
});

export const getEnrolledCoursesFeed = (state, topologyUID) =>
  state.paginator.enrolledCourses[topologyUID] || emptyPaginatedObject;

const getEnrolledCourses = (state, topologyUID) =>
  getEnrolledCoursesFeed(state, topologyUID).results;

export const hasEnrolledCourses = (state, topologyUID) =>
  getEnrolledCourses(state, topologyUID).length > 0;

const getRecommendedCoursesFeed = (state, topologyUID) =>
  state.paginator.recommendedCourses[topologyUID] || emptyPaginatedObject;

export const getRecommendedCourses = (state, topologyUID) =>
  getRecommendedCoursesFeed(state, topologyUID).results;

export const hasRecommendedCourses = (state, topologyUID) =>
  getRecommendedCourses(state, topologyUID).length > 0;

export const getCompletedCoursesFeed = (state, topologyUID) =>
  state.paginator.completedCourses[topologyUID] || emptyPaginatedObject;

const getCompletedCourses = (state, topologyUID) =>
  getCompletedCoursesFeed(state, topologyUID).results;

export const hasCompletedCourses = (state, topologyUID) =>
  getCompletedCourses(state, topologyUID).length > 0;

export const getMoreCoursesFilters = (state, goalUID) =>
  state.courses.moreCoursesFilters[goalUID] || emptyObject;

export const getMoreCoursesFeed = (state, key) =>
  state.paginator.moreCourses[key] || emptyPaginatedObject;

const getPreviousMoreCoursesFeed = (state, key, previousKey) => {
  const newFeed = getMoreCoursesFeed(state, key);
  const previousFeed = getMoreCoursesFeed(state, previousKey);
  return !newFeed.loaded || newFeed.isFetching ? previousFeed : null;
};

export const getCurrentMoreCoursesFeed = (
  state,
  key,
  previousKey,
  areFiltersChanged
) => {
  const moreCoursesFeed = getMoreCoursesFeed(state, key);
  const prevMoreCoursesFeed = getPreviousMoreCoursesFeed(
    state,
    key,
    previousKey
  );
  return (!moreCoursesFeed.loaded || moreCoursesFeed.isFetching) &&
    areFiltersChanged
    ? prevMoreCoursesFeed
    : moreCoursesFeed;
};
// #endregion
